import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL } from '../../utils/api';
import { hamtaBestallarnamn, hamtaSokbartKundnamn } from '../../utils/nameUtils';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/Modal';
import Avatar from '../../components/common/Avatar';
import useVoiceRecognition from '../../features/meetings/hooks/useVoiceRecognition';
import { getBrowserSpeechSupport, isMobileDevice } from '../../features/meetings/utils/speechRecognition';

// Mikrofon-ikon
const MicrophoneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

// Stopp-ikon
const StopIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

/**
 * VoiceMicButton - Kompakt mikrofon-knapp för inputfält
 */
const VoiceMicButton = ({ isListening, isSupported, error, interimTranscript, onStart, onStop, disabled }) => {
  const browserSupport = getBrowserSpeechSupport();

  if (!isSupported) {
    return (
      <button
        type="button"
        disabled
        className="p-1.5 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed"
        title={browserSupport.message}
      >
        <MicrophoneIcon className="w-5 h-5 opacity-50" />
      </button>
    );
  }

  if (isListening) {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onStop}
          className="p-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors animate-pulse"
          title="Stoppa inspelning"
        >
          <StopIcon className="w-5 h-5" />
        </button>
        {interimTranscript && (
          <span className="text-xs text-gray-400 italic max-w-[100px] truncate">
            {interimTranscript}
          </span>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onStart}
      disabled={disabled}
      className={`p-1.5 rounded-lg transition-colors ${
        error
          ? 'bg-red-100 text-red-600 hover:bg-red-200'
          : 'bg-gray-100 text-gray-600 hover:bg-leadme-pink hover:text-white'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      title={error || 'Diktera med rösten'}
    >
      <MicrophoneIcon className="w-5 h-5" />
    </button>
  );
};

function QuickAddTaskModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const isBuilder = user?.role === 'byggare';
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [assignedTo, setAssignedTo] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');

  /**
   * Snabbskapa fungerar för två saker: en uppgift eller en ÄTA.
   * ÄTA skapas alltid som utkast — createATA sätter status draft — så det här
   * är ett sätt att fånga upp något på plats och fylla i detaljerna senare.
   */
  const [läge, setLäge] = useState('uppgift');
  const [ataType, setAtaType] = useState('tillägg');
  const arAta = läge === 'ata';

  // Samma roller som får skapa ÄTA i ATATab
  const kanSkapaAta = ['admin', 'byggkonsult', 'byggare'].includes(user?.role);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Röststyrning
  const isMobile = useMemo(() => isMobileDevice(), []);

  const convertPunctuation = (text) => {
    let result = text;
    result = result.replace(/\bpunkt\b/gi, '.');
    result = result.replace(/\bkomma\b/gi, ',');
    result = result.replace(/\bbindestreck\b/gi, '-');
    result = result.replace(/\bradbryt\b/gi, '\n');
    result = result.replace(/\bny\s*rad\b/gi, '\n');
    result = result.replace(/\bnyrad\b/gi, '\n');
    result = result.replace(/[^\S\n]*\.[^\S\n]*/g, '. ');
    result = result.replace(/[^\S\n]*,[^\S\n]*/g, ', ');
    result = result.replace(/[^\S\n]*-[^\S\n]*/g, '-');
    result = result.replace(/[^\S\n]*\n[^\S\n]*/g, '\n');
    result = result.split('\n').map(line => line.trim()).join('\n');
    result = result.replace(/\.\s*$/g, '.');
    result = result.replace(/,\s*$/g, ',');
    return result;
  };

  // Röstinmatning för rubrik
  const {
    isListening: isTitleListening,
    isSupported: isVoiceSupported,
    interimTranscript: titleInterim,
    error: titleVoiceError,
    startListening: startTitleListening,
    stopListening: stopTitleListening
  } = useVoiceRecognition({
    continuous: !isMobile,
    onResult: (text) => {
      let cleanedText = text;
      const lowerText = text.toLowerCase().trim();
      if (lowerText === 'stopp' || lowerText.endsWith(' stopp')) {
        cleanedText = text.replace(/\s*stopp\s*$/gi, '').trim();
        if (cleanedText) {
          const processedText = convertPunctuation(cleanedText);
          setTitle(prev => prev ? `${prev} ${processedText}` : processedText);
        }
        if (!isMobile) setTimeout(() => stopTitleListening(), 50);
        return;
      }
      if (!cleanedText) return;
      const processedText = convertPunctuation(cleanedText);
      setTitle(prev => prev ? `${prev} ${processedText}` : processedText);
    }
  });

  // Röstinmatning för beskrivning
  const {
    isListening: isDescListening,
    interimTranscript: descInterim,
    error: descVoiceError,
    startListening: startDescListening,
    stopListening: stopDescListening
  } = useVoiceRecognition({
    continuous: !isMobile,
    onResult: (text) => {
      let cleanedText = text;
      const lowerText = text.toLowerCase().trim();
      const shouldStop = lowerText === 'stopp' || lowerText.endsWith(' stopp');
      if (shouldStop) {
        cleanedText = text.replace(/\s*stopp\s*$/gi, '').trim();
      }
      if (cleanedText) {
        const processedText = convertPunctuation(cleanedText);
        if (processedText) {
          setDescription(prev => {
            if (!prev) return processedText;
            if (/^[\n.,-]/.test(processedText)) return prev + processedText;
            if (prev.endsWith('\n')) return prev + processedText;
            return `${prev} ${processedText}`;
          });
        }
      }
      if (shouldStop && !isMobile) setTimeout(() => stopDescListening(), 50);
    }
  });

  // Röstinmatning för kontext/källa
  const {
    isListening: isSourceListening,
    interimTranscript: sourceInterim,
    error: sourceVoiceError,
    startListening: startSourceListening,
    stopListening: stopSourceListening
  } = useVoiceRecognition({
    continuous: !isMobile,
    onResult: (text) => {
      let cleanedText = text;
      const lowerText = text.toLowerCase().trim();
      if (lowerText === 'stopp' || lowerText.endsWith(' stopp')) {
        cleanedText = text.replace(/\s*stopp\s*$/gi, '').trim();
        if (cleanedText) {
          const processedText = convertPunctuation(cleanedText);
          setSourceNote(prev => prev ? `${prev} ${processedText}` : processedText);
        }
        if (!isMobile) setTimeout(() => stopSourceListening(), 50);
        return;
      }
      if (!cleanedText) return;
      const processedText = convertPunctuation(cleanedText);
      setSourceNote(prev => prev ? `${prev} ${processedText}` : processedText);
    }
  });

  const isAnyVoiceActive = isTitleListening || isDescListening || isSourceListening;

  // Hämta projekt när modal öppnas
  useEffect(() => {
    if (!isOpen) return;
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const token = localStorage.getItem('token');
        // excludeCompleted: avslutade projekt hör inte hemma i en snabbskapa-lista.
        // Man lägger inte en ny uppgift eller ÄTA på ett projekt som är stängt, och
        // listan blir ohanterligt lång när avslutade projekt bara växer i antal.
        // Endpointen har stött flaggan hela tiden — modalen skickade den bara aldrig.
        const res = await fetch(`${API_URL}/myassigned/assigned?excludeCompleted=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Backend returnerar { projects: [...], statusCounts: {...} }
          setProjects(Array.isArray(data) ? data : (data?.projects || []));
        }
      } catch (err) {
        console.error('Kunde inte hämta projekt:', err);
      }
      setLoadingProjects(false);
    };
    fetchProjects();
  }, [isOpen]);

  // Hämta medlemmar när projekt väljs
  useEffect(() => {
    if (!selectedProject) {
      setMembers([]);
      return;
    }
    const fetchMembers = async () => {
      setLoadingMembers(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/projektering/${selectedProject._id}/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMembers(data.members || []);
          // Sätt inloggad användare som default ansvarig/tilldela
          setAssignedTo(user?._id);
        }
      } catch (err) {
        console.error('Kunde inte hämta medlemmar:', err);
      }
      setLoadingMembers(false);
    };
    fetchMembers();
  }, [selectedProject, user?._id]);

  // Stäng dropdown vid klick utanför
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProjectDropdown(false);
      }
    };
    if (showProjectDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProjectDropdown]);

  // Filtrerad projektlista
  const filteredProjects = projects.filter(p => {
    if (!projectSearch.trim()) return true;
    const q = projectSearch.toLowerCase();
    // Både föreningen och kontaktpersonen ska ge träff
    const custName = hamtaSokbartKundnamn(p.customer);
    const addr = (p.address || '').toLowerCase();
    const pid = (p.projectId || '').toLowerCase();
    return custName.includes(q) || addr.includes(q) || pid.includes(q) || `#${pid}`.includes(q);
  });

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setProjectSearch('');
    setShowProjectDropdown(false);
    setAssignedTo(null);
    setParticipants([]);
  };

  // Härled uppgiftens type (arbetskategori) från den ansvarige (första tilldelade).
  const roleToType = (role) => {
    if (role === 'inredningsarkitekt' || role === 'inredare') return 'inredare';
    if (role === 'byggare') return 'byggare';
    return 'byggkonsult';
  };

  // Tilldelade personer = EN lista (ansvarig = första, deltagare = övriga).
  const assignedPeople = [assignedTo, ...participants].filter(Boolean);
  const togglePerson = (memberId) => {
    const list = assignedPeople.includes(memberId)
      ? assignedPeople.filter(id => id !== memberId)
      : [...assignedPeople, memberId];
    setAssignedTo(list[0] || null);
    setParticipants(list.slice(1));
  };

  const resetForm = useCallback(() => {
    setSelectedProject(null);
    setProjectSearch('');
    setTitle('');
    setDescription('');
    setSourceNote('');
    setAssignedTo(null);
    setParticipants([]);
    setPriority('medium');
    setDueDate('');
    setError('');
    setSuccess(false);
    setLäge('uppgift');
    setAtaType('tillägg');
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject || !title.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      let res;
      if (arAta) {
        // ÄTA-numret genereras i backend, liksom status draft och en tom
        // prisrad. Kostnader fylls i senare i ÄTA-vyn.
        res = await fetch(`${API_URL}/api/ata`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectId: selectedProject._id,
            title: title.trim(),
            description: description.trim(),
            ataType
          })
        });
      } else if (isBuilder) {
        // Byggare → skapa via Todo API (synkar automatiskt till Kanban)
        const body = {
          projectId: selectedProject._id,
          title: title.trim(),
          content: description.trim() || undefined,
          dueDate: dueDate || undefined,
          priority,
          participants: participants.length > 0 ? participants : undefined,
        };
        // Om tilldelad till annan person, skicka ownerId
        if (assignedTo && assignedTo !== user._id) {
          body.ownerId = assignedTo;
        }

        res = await fetch(`${API_URL}/api/todos`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
      } else {
        // BK/IA → skapa via Kanban API. Type härleds från ansvarig (första tilldelade),
        // fallback = skaparens roll.
        const respRole = members.find(m => m._id === assignedTo)?.role;
        const taskType = respRole ? roleToType(respRole) : (user.role === 'inredningsarkitekt' ? 'inredare' : 'byggkonsult');

        const body = {
          title: title.trim(),
          description: description.trim(),
          column: 'attgora',
          type: taskType,
          priority,
          assignedTo: assignedTo || undefined,
          participants: participants.length > 0 ? participants : undefined,
          sourceNote: sourceNote.trim() || undefined,
          dueDate: dueDate || undefined,
          createdBy: user._id
        };

        res = await fetch(`${API_URL}/projektering/${selectedProject._id}/task`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
      }

      if (res.ok) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1200);
      } else {
        const data = await res.json();
        setError(data.message || (arAta ? 'Kunde inte skapa ÄTA' : 'Kunde inte skapa uppgift'));
      }
    } catch (err) {
      setError('Något gick fel');
    }
    setSubmitting(false);
  };

  const getRoleBadge = (member) => {
    const colors = {
      'BK': 'bg-blue-100 text-blue-700',
      'IA': 'bg-purple-100 text-purple-700',
      'Byggare': 'bg-orange-100 text-orange-700',
      'UE': 'bg-yellow-100 text-yellow-700',
      'Projektör': 'bg-green-100 text-green-700',
      'Kund': 'bg-gray-100 text-gray-700'
    };
    return colors[member.roleLabel] || 'bg-gray-100 text-gray-600';
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="medium">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">{arAta ? 'Snabbskapa ÄTA' : 'Snabbskapa uppgift'}</h2>

        {/* Lägesväljare — bara för roller som får skapa ÄTA */}
        {kanSkapaAta && !success && (
          <div className="flex rounded-lg border border-gray-300 overflow-hidden mb-4 w-fit">
            {[{ v: 'uppgift', t: 'Uppgift' }, { v: 'ata', t: 'ÄTA' }].map(o => (
              <button
                key={o.v}
                type="button"
                onClick={() => setLäge(o.v)}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                  läge === o.v ? 'bg-leadme-pink text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {o.t}
              </button>
            ))}
          </div>
        )}

        {success ? (
          <div className="text-center py-8">
            <svg className="w-14 h-14 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-gray-700">{arAta ? 'ÄTA skapad som utkast!' : 'Uppgift skapad!'}</p>
            {arAta && (
              <p className="text-xs text-gray-500 mt-1">Fyll i priser och skicka den från ÄTA-fliken.</p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Projektväljare */}
            <div ref={dropdownRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Projekt <span className="text-red-500">*</span>
              </label>
              {selectedProject ? (
                <div className="flex items-center justify-between bg-gray-50 border border-gray-300 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-gray-800">#{selectedProject.projectId}</span>
                    {selectedProject.customer && (
                      <span className="text-sm text-gray-600 ml-2">
                        {hamtaBestallarnamn(selectedProject.customer)}
                      </span>
                    )}
                    {selectedProject.address && (
                      <span className="text-xs text-gray-400 ml-2">{selectedProject.address}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedProject(null); setMembers([]); setAssignedTo(null); setParticipants([]); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={searchRef}
                    type="text"
                    value={projectSearch}
                    onChange={(e) => { setProjectSearch(e.target.value); setShowProjectDropdown(true); }}
                    onFocus={() => setShowProjectDropdown(true)}
                    placeholder={loadingProjects ? 'Laddar projekt...' : 'Sök på kundnamn, adress eller #projektId...'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                  />
                  {showProjectDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredProjects.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-400">Inga projekt hittades</div>
                      ) : (
                        filteredProjects.map(p => (
                          <button
                            key={p._id}
                            type="button"
                            onClick={() => handleSelectProject(p)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-0"
                          >
                            <span className="font-medium text-gray-800">#{p.projectId}</span>
                            {p.customer && (
                              <span className="text-gray-600 ml-2">
                                {hamtaBestallarnamn(p.customer)}
                              </span>
                            )}
                            {p.address && (
                              <span className="text-gray-400 ml-2 text-xs">{p.address}</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Rubrik */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rubrik <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={isTitleListening ? `${title}${titleInterim ? ` ${titleInterim}` : ''}` : title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isTitleListening}
                  placeholder="Vad ska göras?"
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink ${
                    isTitleListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  required
                />
                <VoiceMicButton
                  isListening={isTitleListening}
                  isSupported={isVoiceSupported}
                  error={titleVoiceError}
                  interimTranscript={titleInterim}
                  onStart={startTitleListening}
                  onStop={stopTitleListening}
                  disabled={submitting || (isAnyVoiceActive && !isTitleListening)}
                />
              </div>
            </div>

            {/* Beskrivning */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivning</label>
              <div className="flex gap-2">
                <textarea
                  value={isDescListening ? `${description}${descInterim ? ` ${descInterim}` : ''}` : description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isDescListening}
                  placeholder="Detaljerad beskrivning (valfritt)"
                  rows={2}
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink ${
                    isDescListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <div className="flex-shrink-0 pt-1">
                  <VoiceMicButton
                    isListening={isDescListening}
                    isSupported={isVoiceSupported}
                    error={descVoiceError}
                    interimTranscript={descInterim}
                    onStart={startDescListening}
                    onStop={stopDescListening}
                    disabled={submitting || (isAnyVoiceActive && !isDescListening)}
                  />
                </div>
              </div>
            </div>

            {/* Kontext / Källa (ej för byggare, ej för ÄTA) */}
            {!isBuilder && !arAta && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kontext / Källa</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={isSourceListening ? `${sourceNote}${sourceInterim ? ` ${sourceInterim}` : ''}` : sourceNote}
                  onChange={(e) => setSourceNote(e.target.value)}
                  disabled={isSourceListening}
                  placeholder="T.ex. Telefonsamtal med kund 2026-02-23"
                  className={`flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink ${
                    isSourceListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <VoiceMicButton
                  isListening={isSourceListening}
                  isSupported={isVoiceSupported}
                  error={sourceVoiceError}
                  interimTranscript={sourceInterim}
                  onStart={startSourceListening}
                  onStop={stopSourceListening}
                  disabled={submitting || (isAnyVoiceActive && !isSourceListening)}
                />
              </div>
            </div>
            )}

            {/* ÄTA-typ */}
            {arAta && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                <select
                  value={ataType}
                  onChange={(ev) => setAtaType(ev.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                >
                  <option value="tillägg">Tillägg</option>
                  <option value="ändring">Ändring</option>
                  <option value="avgående">Avgående</option>
                  <option value="inköp">Inköp</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Skapas som utkast. Priser och tidstillägg fyller du i från ÄTA-fliken.
                </p>
              </div>
            )}

            {/* Prioritet (visas efter projektval) */}
            {!arAta && selectedProject && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioritet</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                >
                  <option value="low">Låg</option>
                  <option value="medium">Medel</option>
                  <option value="high">Hög</option>
                  <option value="urgent">Brådskande</option>
                </select>
              </div>
            )}

            {/* Tilldelade personer — alla på listan ser uppgiften och kan markera den klar.
                Gäller inte ÄTA: den har ingen ansvarig, den skickas till kund. */}
            {!arAta && selectedProject && !loadingMembers && members.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tilldelade personer</label>
                <div className="flex flex-wrap gap-2">
                  {members.map(m => {
                    const isSelected = assignedPeople.includes(m._id);
                    return (
                      <button
                        key={m._id}
                        type="button"
                        onClick={() => togglePerson(m._id)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                          isSelected
                            ? 'bg-leadme-pink/10 border-leadme-pink text-leadme-pink'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Avatar entity={m} size="xs" />
                        <span>{m.firstName} {m.lastName}</span>
                        <span className={`text-[10px] px-1 py-0.5 rounded ${getRoleBadge(m)}`}>{m.roleLabel}</span>
                        {isSelected && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Deadline — ÄTA har svarsdeadline i stället, satt vid utskick */}
            {!arAta && selectedProject && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink"
                />
              </div>
            )}

            {/* Felmeddelande */}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</div>
            )}

            {/* Knappar */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-2.5 py-1.5 sm:px-3 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedProject || !title.trim()}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                  submitting || !selectedProject || !title.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-leadme-pink hover:bg-leadme-pink-dark'
                }`}
              >
                {submitting ? 'Skapar...' : (arAta ? 'Skapa ÄTA-utkast' : 'Skapa uppgift')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}

export default QuickAddTaskModal;
